import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'antd';
import { MarketplaceRecordWithRatings } from '@kemu-io/kemu-types';
import Card from 'antd/es/card/Card';
import { Empty } from 'antd/lib';
import styles from './HubServicesScreen.module.css';
import HubServiceCard from './HubServiceCard/HubServiceCard';
import HubServiceDetailsPage from './HubServiceDetailsPage/HubServiceDetailsPage';
import HubServiceCardSkeleton from './HubServiceCardSkeleton/HubServiceCardSkeleton';
import useQueryString from '@common/hooks/useQueryString';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { SearchInputContextType } from '@src/contexts/SearchInputContext';
import useTranslation from '@hooks/useTranslation';
import useMarketplaceFetch from '@hooks/useMarketplaceFetch';
import useHubSystemInfo from '@hooks/useHubSystemInfo';
import useMarketplaceNavigator, { BasicServiceInfo, decodeServiceInfo } from '@hooks/useMarketplaceNavigator';
import { DefaultPlatformArch } from '@common/constants';
import useGetPendingServiceInstallation from '@hooks/useGetPendingServiceInstallation';
import useSubscriptionInfo from '@hooks/useSubscriptionInfo';
import UpgradeCtaModal from '@src/features/CheckoutModal/UpgradeCtaModal/UpgradeCtaModal';
import { useDispatch } from 'react-redux';
import { openCheckoutModalAction } from '@src/app/reducers/checkout/checkoutSlice';
import { useHubContext } from '@common/context/HubContext/HubContext';

const GridResults = (props: {
	hits: MarketplaceRecordWithRatings[];
	hitRenderer: (hit: MarketplaceRecordWithRatings) => React.JSX.Element;
}) => {
	const { hits, hitRenderer } = props;

	return (
		<div style={{ maxWidth: '1500px', margin: '0 auto', height: '100%' }}>
			<Row
				gutter={[
					{ xs: 0, sm: 12, md: 16, lg: 16, xl: 24 },
					{ xs: 16, sm: 20, md: 24, lg: 24, xl: 32 }
				]}
				className={styles.KemuRow}
				justify={'start'}
			>
				{hits.map((hit) => (
					<Col key={hit.id} xs={24} sm={12} md={12} lg={8} xl={8} xxl={5} style={{ minWidth: '465px' }}>
						{hitRenderer(hit)}
					</Col>
				))}
			</Row>
		</div>
	);
};


type Props = {
	onSetSearchContext: (context: SearchInputContextType) => void;
	onCloseMarketplace: () => void;
};


const resultsPerPage = 100;

const HubServicesScreen = ({ onSetSearchContext, onCloseMarketplace }: Props): React.JSX.Element | null => {
	const queryStr = useQueryString();
	const paramServiceInfo = decodeServiceInfo(queryStr.get('hs'));
	const fetchMarketplace = useMarketplaceFetch();
	const [results, setResults] = useState<MarketplaceRecordWithRatings[] | null>(null);
	const [selectedService, setSelectedService] = useState<BasicServiceInfo | null>(null);
	const hubSystemInfo = useHubSystemInfo();
	const { pendingServices, isLoading: isPendingServicesLoading, forceReload: forceReloadPendingServices } = useGetPendingServiceInstallation();
	const platformArch = hubSystemInfo?.platformArch || DefaultPlatformArch;
	const containerRef = useRef<HTMLDivElement>(null);
	const [isLoading, setIsLoading] = useState(true);
	const fetchFrom = useRef(0);
	const finishedResults = useRef(false);
	const lastQuery = useRef('');
	const { navigateToService: updateSelectedService } = useMarketplaceNavigator();
	const { services } = useHubContext();
	const subscriptionInfo = useSubscriptionInfo();
	const [showUpgradeCta, setShowUpgradeCta] = useState(false);
	const dispatch = useDispatch();

	const t = useTranslation('Marketplace.HubServices');
	const officialServices = services.available.filter((service) => !service.internal && !service.devMode && service.name.startsWith('kemu.io')) || [];
	const remainingOfficialServices = officialServices.length;
	const canInstallMoreServices = (subscriptionInfo?.features?.['hub_services'] || 0) > remainingOfficialServices;

	const requestUpgrade = useCallback(() => {
		setShowUpgradeCta(true);
	}, []);

	const handleCtaCancel = useCallback(() => setShowUpgradeCta(false), []);
	const handleCtaUpgrade = useCallback(() => {
		// Close marketplace modal
		setShowUpgradeCta(false);
		onCloseMarketplace();
		dispatch(openCheckoutModalAction());
	}, [dispatch]);



	const handleLoadDetails = useCallback((serviceInfo: MarketplaceRecordWithRatings) => {
		// Add selected service to the query string
		updateSelectedService({
			name: serviceInfo.name,
			version: serviceInfo.version,
			platform: serviceInfo.platform
		});
		// setSelectedService(serviceInfo);
	}, [updateSelectedService]);

	const fetchNextResults = useCallback(async (reset: boolean = false) => {
		if (finishedResults.current) {
			return;
		}

		setIsLoading(true);
		const results = await fetchMarketplace({
			query: lastQuery.current,
			from: fetchFrom.current,
			total: resultsPerPage,
			platform: platformArch,
		}).finally(() => {
			setIsLoading(false);
		});

		if (results.length < resultsPerPage) {
			finishedResults.current = true;
		}
		setResults((c) => [...(reset ? [] : c || []), ...results]);
	}, [fetchMarketplace, platformArch]);

	const handleGlobalSearch = useCallback(async (query: string) => {
		lastQuery.current = query;
		finishedResults.current = false;
		fetchFrom.current = 0;
		fetchNextResults(true);
	}, [fetchNextResults]);


	const handleGoBack = useCallback(() => {
		updateSelectedService(null);
		setSelectedService(null);
	}, [updateSelectedService]);

	const handleNavigateToVersion = useCallback((serviceInfo: BasicServiceInfo) => {
		updateSelectedService({
			name: serviceInfo.name,
			version: serviceInfo.version,
			platform: serviceInfo.platform
		});

		setSelectedService(serviceInfo);
	}, [updateSelectedService]);

	/* function handleScroll() {
		const parent = containerRef.current?.parentElement;
		if (parent) {
			console.log('handleScroll', parent.scrollTop, parent.clientHeight, parent.scrollHeight);
			if (parent.scrollTop + parent.clientHeight !== parent.scrollHeight) {
				return;
			}

			if (isLoading) {
				return;	
			}

			console.log('Fetch more list items!');
			fetchFrom.current += 1;
			fetchNextResults();
		}
	} */

	// Register the search handler when the component mounts
	useEffect(() => {
		onSetSearchContext?.({
			onSearch: handleGlobalSearch,
		});
	}, [onSetSearchContext, handleGlobalSearch]);

	useEffect(() => {
		const selectedServiceDifferent = selectedService?.name !== paramServiceInfo?.name
			|| selectedService?.version !== paramServiceInfo?.version
			|| selectedService?.platform !== paramServiceInfo?.platform;

		if (paramServiceInfo && !selectedService || selectedServiceDifferent) {
			setSelectedService(paramServiceInfo);
		} else if (!paramServiceInfo && selectedService) {
			setSelectedService(null);
		}

	}, [paramServiceInfo, selectedService]);

	// Reloads the pending services list after coming out of a details page
	useEffect(() => {
		if (selectedService === null) {
			forceReloadPendingServices();
		}
	}, [forceReloadPendingServices, selectedService]);


	// if(paramServiceId && !selectedService) {
	// 	return (
	// 		<div>Loading details....</div>
	// 		// <div className={styles.HubServicesScreen}>
	// 		// 	<GridResults
	// 		// 		hits={Array(Math.min(resultsPerPage, 15)).fill('x')}
	// 		// 		hitRenderer={HubServiceCardSkeleton}
	// 		// 	/>
	// 		// </div>
	// 	)	
	// }
	const isInstalling = (serviceInfo: MarketplaceRecordWithRatings) => {
		if (isPendingServicesLoading) { return false; }
    const status = Object.values(pendingServices).find(service => service.name === serviceInfo.name && service.version === serviceInfo.version)?.status;
    return status === 'installing' || status === 'launching';
  };

	if (selectedService) {
		return (
			<>
				<UpgradeCtaModal
					visible={showUpgradeCta}
					onCancel={handleCtaCancel}
					onUpgrade={handleCtaUpgrade}
				/>

				<HubServiceDetailsPage
					info={selectedService}
					onGoBack={handleGoBack}
					onNavigateToVersion={handleNavigateToVersion}
					canInstallMoreServices={canInstallMoreServices}
					onRequestUpgrade={requestUpgrade}
				/>;
			</>
		)
	}

	// console.log('pendingServices: ', pendingServices, 'isPendingServicesLoading: ', isPendingServicesLoading);
	return (
		<div className={styles.HubServicesScreen} ref={containerRef}>
			{/* Todo load results based on scroll factor */}
			{/* {!!results?.documents.length && (
				<StyledPagination
					wrapperClassName={styles.PaginationWrapper}
					current={results.page}
					total={results.total}
					onChange={handlePageChange}
					pageSize={resultsPerPage}
					showSizeChanger={false}
				/>
			)} */}

			<UpgradeCtaModal
				visible={showUpgradeCta}
				onCancel={handleCtaCancel}
				onUpgrade={handleCtaUpgrade}
			/>

			<GridResults
				hits={results || []}
				hitRenderer={
					(hit) => (
						<HubServiceCard
							serviceInfo={hit}
							// isInstalling={isInstalling(hit)}
							isInstalling={isInstalling(hit)}
							onLoadDetails={handleLoadDetails}
							canInstallMoreServices={canInstallMoreServices}
							onRequestUpgrade={requestUpgrade}
						/>
					)
				}
			/>


			{isLoading && (
				<div className={styles.HubServicesScreen}>
					<GridResults
						hits={Array(Math.min(resultsPerPage, 15)).fill('x')}
						hitRenderer={HubServiceCardSkeleton}
					/>
				</div>
			)}

			{results && results.length === 0 && (
				<Card>
					<div className={styles.NoResults}>
						<Empty description={t('NoneFound')} />
					</div>
				</Card>
			)}


		</div>
  );
};


export default HubServicesScreen;
